import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from './Button';
import Wizard from './Wizard';
import { ZapIcon, ArrowRightIcon, GlobeIcon, ShareIcon, MessagesSquareIcon, PhoneCallIcon, BotIcon, PhoneIcon, PlayIcon } from 'lucide-react';
import ConversationNotifier from './ConversationNotifier';
import { useTranslation } from '../i18n/useTranslation';
const HeroAlt = () => {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedAgentType, setSelectedAgentType] = useState('');
  const navigate = useNavigate();
  const {
    t
  } = useTranslation();
  const formRef = useRef<HTMLElement>(null);
  const handleAgentSelection = agentType => {
    setSelectedAgentType(agentType);
    setIsWizardOpen(true);
  };
  const handleLearnMore = () => {
    navigate('/gpt-store');
    window.scrollTo(0, 0);
  };
  const scrollToForm = () => {
    // Try to find a form element on the page to scroll to
    const formElement = document.querySelector('form') || document.querySelector('#contact-form');
    if (formElement) {
      formElement.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      // If no form found, just open the wizard as fallback
      setIsWizardOpen(true);
    }
  };
  return <section className="relative py-24 overflow-hidden bg-white text-gray-800" data-id="element-1763">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1764">
        {/* Main headline */}
        <div className="max-w-4xl mx-auto text-center mb-10" data-id="element-1765">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rhREvAJ4b5296S85upFAnM/logonew.png" alt="Shopilots Logo" className="h-16 mx-auto mb-8" data-id="element-1766" />
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 text-gray-900" data-id="element-1767">
            {t('hero.title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-1768">
              {t('hero.subtitle')}
            </span>
          </h1>
          <p className="text-xl text-gray-700 mb-10" data-id="element-1769">{t('hero.description')}</p>
        </div>

        {/* Dynamic Embedding Illustration */}
        <div className="max-w-5xl mx-auto mb-16" data-id="element-1770">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9sv17hTRttNgTQbi53VcZN/ChatGPT_Image_Sep_9%2C_2025%2C_11_31_52_PM.png" alt="Moving from static to dynamic pages with AI-enabled embedding for any channel" className="w-full h-auto rounded-xl shadow-lg border border-gray-200" data-id="element-1771" />
        </div>

        {/* Video Demo Section */}
        <div className="max-w-6xl mx-auto mb-16" data-id="element-1772">
          {/* Mobile: Full width video */}
          <div className="lg:hidden w-full" data-id="element-1773">
            <div className="relative rounded-xl overflow-hidden shadow-lg bg-black" data-id="element-1774">
              <div className="w-full" style={{
              paddingBottom: '177.78%'
            }} data-id="element-1775">
                <iframe className="absolute inset-0 w-full h-full" src="https://app.heygen.com/embedded-player/c820e5db87424fa4a7df4dd131e843c0" title="HeyGen video player" frameBorder="0" allow="encrypted-media; fullscreen;" allowFullScreen data-id="element-1776"></iframe>
              </div>
            </div>
          </div>

          {/* Desktop: Phone mockup frame centered */}
          <div className="hidden lg:flex justify-center items-center" data-id="element-1777">
            <div className="relative" data-id="element-1778">
              {/* Phone frame mockup */}
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-[3rem] p-3 shadow-2xl" data-id="element-1779">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-40 h-7 bg-black rounded-b-3xl z-10" data-id="element-1780"></div>

                {/* Screen */}
                <div className="relative bg-black rounded-[2.5rem] overflow-hidden" style={{
                width: '375px',
                height: '667px'
              }} data-id="element-1781">
                  <iframe className="w-full h-full" src="https://app.heygen.com/embedded-player/c820e5db87424fa4a7df4dd131e843c0" title="HeyGen video player" frameBorder="0" allow="encrypted-media; fullscreen;" allowFullScreen data-id="element-1782"></iframe>
                </div>

                {/* Home indicator */}
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-600 rounded-full" data-id="element-1783"></div>
              </div>

              {/* Decorative glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-[3rem] blur-2xl -z-10" data-id="element-1784"></div>
            </div>
          </div>

          <div className="text-center mt-6" data-id="element-1785">
            <p className="text-sm text-gray-600 italic" data-id="element-1786">
              "See how our AI shopping assistant helps customers find the
              perfect products, answers questions, and drives sales across all
              platforms."
            </p>
          </div>
        </div>

        {/* Agent selection options */}
        <div className="max-w-5xl mx-auto" data-id="element-1787">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-800" data-id="element-1788">
            What would you like to build?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-1789">
            <button onClick={() => handleAgentSelection('website')} className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1790">
              <GlobeIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1791" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1792">
                Website Agent
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1793">
                Embed on your e-commerce site
              </p>
            </button>

            <button onClick={() => handleAgentSelection('social')} className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1794">
              <ShareIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1795" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1796">
                Social Media Agent
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1797">
                Engage on social platforms
              </p>
            </button>

            <button onClick={() => handleAgentSelection('messenger')} className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1798">
              <MessagesSquareIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1799" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1800">
                Messenger Agent
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1801">
                Chat on WhatsApp & Messenger
              </p>
            </button>

            <button onClick={() => handleAgentSelection('call')} className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1802">
              <PhoneCallIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1803" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1804">Call Agent</h3>
              <p className="text-sm text-gray-600" data-id="element-1805">
                Handle customer phone calls
              </p>
            </button>

            <button onClick={() => handleAgentSelection('gpt')} className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1806">
              <BotIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1807" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1808">GPT Store</h3>
              <p className="text-sm text-gray-600" data-id="element-1809">
                Launch in ChatGPT & GPT Store
              </p>
            </button>
          </div>

          <div className="text-center mt-12" data-id="element-1810">
            <a href="tel:+19146011670" className="inline-flex items-center px-6 py-3 rounded-md bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg transition-all duration-300 font-medium" data-id="element-1811">
              <PhoneIcon className="mr-2 h-5 w-5" data-id="element-1812" />
              Call (914) 601-1670
            </a>
            <button onClick={handleLearnMore} className="ml-4 inline-flex items-center px-6 py-3 rounded-md bg-white text-purple-600 border-2 border-purple-600 hover:bg-gray-50 shadow-md hover:shadow-lg transition-all duration-300 font-medium" data-id="element-1813">
              Learn More
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1814" />
            </button>
          </div>
        </div>

        {/* Partner logos */}
        <div className="max-w-4xl mx-auto text-center mt-20" data-id="element-1815">
          <p className="text-sm text-gray-600 mb-4" data-id="element-1816">
            Integrate with your existing platforms and AI partners
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 opacity-80" data-id="element-1817">
            <img src="https://cdn.worldvectorlogo.com/logos/shopify.svg" alt="Shopify" className="h-7" data-id="element-1818" />
            <img src="https://cdn.worldvectorlogo.com/logos/woocommerce.svg" alt="WooCommerce" className="h-7" data-id="element-1819" />
            <img src="https://cdn.worldvectorlogo.com/logos/salesforce-2.svg" alt="Salesforce" className="h-7" data-id="element-1820" />
            <img src="https://cdn.worldvectorlogo.com/logos/openai-2.svg" alt="OpenAI" className="h-7" data-id="element-1821" />
            <img src="https://cdn.worldvectorlogo.com/logos/aws-2.svg" alt="Amazon AWS" className="h-7" data-id="element-1822" />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-4 justify-end" data-id="element-1823">
          <a href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3d8hIh-SBmquhBLzT0K2NNUsyp9W7OJz3kZC5dS4y2t7kSLPqcz7Ll5kRBTrX_hUBi2XAKTHHq" target="_blank" rel="noreferrer" className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all" data-id="element-1824">
            {t('hero.bookDemo')}
          </a>
          <button onClick={scrollToForm} className="px-6 py-3 bg-gray-100 border border-gray-200 rounded-xl text-slate-700 font-bold hover:bg-gray-200 transition-all" data-id="element-1825">
            {t('hero.createAssistant')}
          </button>
        </div>
      </div>

      {/* Add the conversation notifier component */}
      <ConversationNotifier data-id="element-1826" />

      <Wizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} initialAgentType={selectedAgentType} data-id="element-1827" />
    </section>;
};
export default HeroAlt;